/* Billing by Phase (Duncan 2026-10-16): which trade each line of an AIA continuation sheet bills
   toward, from the words in its description. Lines aren't named the same way twice ("LED",
   "Light Poles", even "Ligh Poles" on Chico ISD's AFP 7), so each trade has a set of words to look
   for; the first trade whose words match wins, and a line no trade matches is Unassigned, to be
   reviewed rather than guessed. An L job is all lighting. The trades are BoMoCo's departments
   (server/ceDepartments.js), by CE phase. Edit the words here as new names turn up. */

export const LIGHTING = '3';
export const DEV_ENG = '1';
export const UNASSIGNED = 'unassigned';

export const TRADES = [
  // "ligh" catches Lighting, Light Poles and the "Ligh Poles" typo. Not "fixtures" or "poles" on
  // their own: Summit's schedules have "Plumbing - Fixtures & Trim" (2026-10-16).
  { phase: '3', label: 'E3 Lighting', words: /\b(ligh\w*|leds?|lamps?|luminaires?|e3l)\b/i },
  { phase: '5', label: 'E3 EMS', words: /\b(ems|controls?|bas|ddc|automation)\b/i },
  { phase: '4', label: 'Construction', words: /\b(hvac|mechanical|roof\w*|solar|plumbing|construction|mini[- ]?splits?|chillers?|boilers?|rtus?)\b/i },
  { phase: '2', label: 'PM Setup', words: /\b(bonds?|ins|insurance|mobilization)\b/i },
  { phase: '1', label: 'Dev & Eng', words: /\b(design|engineering|development)\b/i },
  { phase: '6', label: 'RCx', words: /\b(rcx|commissioning)\b/i },
  { phase: '7', label: 'Perf Mgmt', words: /\b(m&v|measurement|verification)\b/i },
  { phase: '8', label: 'Warranty', words: /\bwarranty\b/i },
];
export const TRADE_OPTIONS = [...TRADES.map((t) => ({ value: t.phase, label: t.label })), { value: UNASSIGNED, label: 'Unassigned lines' }];

/* An engineering line is Dev & Eng whatever else it names (Duncan 2026-10-19: "engineering lines ...
   go to Dev & Eng"): "Engineering Services for the Bryan ISD - Bond HVAC 2026 project", "Professional
   Engineering Sevices including Districtwide LED Lighting", "Professional Services Agreement", "Design
   Development Progress Fee" -- and CE's typos ("Profesioinal Engieering"). Not "engine" (generators). */
const ENGINEERING_LINE = /\b(engineer\w*|engieer\w*|enginer\w*|design\s+development|professional\s+serv\w*)/i;

/* ...unless it's engineering for lighting work alone (Duncan 2026-10-19, line by line): Jacksboro ISD
   E3L's "Professional Engineering Services for the Preparation of the UAR ... including Lighting Retrofits
   and Upgrades" is E3 Lighting; Chico's "engineering services for the Chico ISD Bond HVAC, Plumbing,
   Lighting and Electrical Projects" and Dalhart's "Districtwide LED Lighting Retrofit, HVAC
   Replacements, HVAC Controls Upgrades" are Dev & Eng. The work trades it may name besides lighting:
   EMS, Construction, RCx, Perf Mgmt (not "Bond" -- a bond program -- nor warranty). */
const OTHER_WORK = ['5', '4', '6', '7'];
const engineeringTrade = (d) => (TRADES.find((t) => t.phase === LIGHTING).words.test(d)
  && !TRADES.some((t) => OTHER_WORK.includes(t.phase) && t.words.test(d)) ? LIGHTING : DEV_ENG);

export const tradeOf = (description) => {
  const d = String(description || '');
  if (ENGINEERING_LINE.test(d)) return engineeringTrade(d);
  const t = TRADES.find((x) => x.words.test(d));
  return t ? t.phase : UNASSIGNED;
};

/* A generic line ("High School Labor", "Material") takes its job's trade from the job's name
   (Duncan 2026-10-16: "use the job name as a fallback"): "Anson ISD - Gym HVAC" is Construction,
   "Jacksboro ISD E3L" lighting. Only the trades a job is named for: "Bond" in a job name is the
   school bond ("Chico ISD - Bond 2024"), not a bond line. */
const NAME_TRADES = ['3', '5', '4', '6'];
export const tradeOfJobName = (name) => {
  const t = TRADES.find((x) => NAME_TRADES.includes(x.phase) && x.words.test(String(name || '')));
  return t ? t.phase : UNASSIGNED;
};

/* An L job is a lighting job: everything it bills is lighting. */
export const isLightingJob = (job) => /-L-/i.test(String(job || ''));
/* An E job (the EFA / assessment jobs) is engineering: everything it bills is Dev & Eng (Duncan 2026-10-19). */
export const isEngineeringJob = (job) => /-E-/i.test(String(job || ''));
/* A job billed to one trade in full, whatever its lines say: L jobs lighting, E jobs Dev & Eng. Else null. */
export const wholeJobTrade = (job) => (isLightingJob(job) ? LIGHTING : isEngineeringJob(job) ? DEV_ENG : null);

const cents = (n) => Math.round(n * 100) / 100;

/* What one invoice billed toward each trade, net of retainage.
   - an L job: its whole invoice is lighting; an E job: its whole invoice is Dev & Eng
   - otherwise its continuation-sheet lines (from GET /api/invoice-history/lines), by trade, a
     line without a trade word taking the job name's; no lines (not read yet, or not an AIA
     invoice) = not split
   Returns { split: false } or { split: true, byTrade: { phase: amount }, unmatched: [descriptions] }. */
export function splitInvoice(job, inv, sheet, jobName) {
  const whole = wholeJobTrade(job);
  if (whole) return { split: true, byTrade: { [whole]: inv.amount }, unmatched: [] };
  if (!sheet || !sheet.lines || !sheet.lines.length) return { split: false };
  const byTrade = {};
  const unmatched = [];
  tradeLines(sheet, jobName).forEach(({ description, net, trade }) => {
    byTrade[trade] = cents((byTrade[trade] || 0) + net);
    if (trade === UNASSIGNED && Math.abs(net) > 0.004) unmatched.push(description);
  });
  return { split: true, byTrade, unmatched };
}

/* Each line of an invoice's sheet with the trade it bills toward -- what splitInvoice adds up, and
   what Billing by Phase's drop-down shows line by line (Duncan 2026-10-19: "show you how it got to
   that billed number"). byName: the line has no trade word, so the job's name gave it its trade.
   Returns [{ item, description, net, trade, byName }]. */
export function tradeLines(sheet, jobName) {
  const fallback = tradeOfJobName(jobName);
  return ((sheet && sheet.lines) || []).map(([item, description, net]) => {
    const own = tradeOf(description);
    return { item, description, net, trade: own === UNASSIGNED ? fallback : own, byName: own === UNASSIGNED && fallback !== UNASSIGNED };
  });
}

/* The label of a trade (a CE phase, or UNASSIGNED) for the screen. */
export const tradeLabelOf = (phase) => (phase === UNASSIGNED ? 'Unassigned' : (TRADES.find((t) => t.phase === phase) || {}).label || phase);
